import {
  supabase,
} from '../firebase/config';
import type { CartItem } from '../contexts/CartContext';
import { sumLineShippingCosts } from '../utils/productShipping';

export interface ShippingAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface OrderCustomer {
  customerId: string;
  customerEmail: string;
  customerName: string;
  customerPhone: string;
}

export interface OrderLineItem {
  productId: string;
  vendorId: string;
  name: string;
  quantity: number;
  price: number;
  total: number;
  image?: string;
  sku?: string;
  category?: string;
}

export interface VendorOrder {
  vendorId: string;
  vendorName: string;
  vendorEmail: string;
  items: OrderLineItem[];
  subtotal: number;
  shippingCost: number;
  total: number;
  platformFee: number;
  vendorEarnings: number;
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
}

export interface Order {
  id: string;
  customerId: string;
  customerEmail: string;
  customerName: string;
  customerPhone: string;
  shippingAddress: ShippingAddress;
  items: OrderLineItem[];
  vendorOrders: VendorOrder[];
  subtotal: number;
  shippingTotal: number;
  totalAmount: number;
  paymentIntentId: string;
  paymentStatus: 'pending' | 'succeeded' | 'failed';
  status: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  createdAt: Date;
  updatedAt: Date;
}

const PLATFORM_FEE_RATE = 0.05;

const toDate = (value: unknown): Date => (typeof value === 'string' ? new Date(value) : new Date());

const mapOrderRow = (data: Record<string, unknown>): Order => ({
  id: String(data.id || ''),
  customerId: String(data.customer_id || ''),
  customerEmail: String(data.customer_email || ''),
  customerName: String(data.customer_name || ''),
  customerPhone: String(data.customer_phone || ''),
  shippingAddress: (data.shipping_address || {}) as ShippingAddress,
  items: (data.items || []) as OrderLineItem[],
  vendorOrders: (data.vendor_orders || []) as VendorOrder[],
  subtotal: Number(data.subtotal || 0),
  shippingTotal: Number(data.shipping_total || 0),
  totalAmount: Number(data.total_amount || 0),
  paymentIntentId: String(data.payment_intent_id || ''),
  paymentStatus: (data.payment_status || 'pending') as Order['paymentStatus'],
  status: (data.status || 'pending') as Order['status'],
  createdAt: toDate(data.created_at),
  updatedAt: toDate(data.updated_at),
});

const mapCartItemToOrderItem = (item: CartItem): OrderLineItem => ({
  productId: item.productId,
  vendorId: item.vendorId,
  name: item.name,
  quantity: item.quantity,
  price: item.price,
  total: item.total,
  image: item.image,
  sku: item.sku,
  category: item.category,
});

export class OrderService {
  private static instance: OrderService;

  public static getInstance(): OrderService {
    if (!OrderService.instance) {
      OrderService.instance = new OrderService();
    }
    return OrderService.instance;
  }

  private async buildOrderPayload(
    cartItems: CartItem[],
    customer: OrderCustomer,
    shippingAddress: ShippingAddress,
    paymentIntentId: string
  ) {
    const vendorGroups = cartItems.reduce<Record<string, CartItem[]>>((acc, item) => {
      if (!acc[item.vendorId]) acc[item.vendorId] = [];
      acc[item.vendorId].push(item);
      return acc;
    }, {});

    const vendorIds = Object.keys(vendorGroups);
    const { data: vendorRows } = await supabase
      .from('vendors')
      .select('user_id,business_name,business_info')
      .in('user_id', vendorIds);
    const vendorMeta = new Map(
      (vendorRows || []).map((row: any) => [
        row.user_id,
        {
          businessName: row.business_name || `Vendor ${String(row.user_id).slice(0, 6)}`,
          email: row.business_info?.email || '',
        },
      ])
    );

    const vendorOrders: VendorOrder[] = Object.entries(vendorGroups).map(([vendorId, items]) => {
      const subtotal = items.reduce((sum, item) => sum + item.total, 0);
      const shippingCost = sumLineShippingCosts(items);
      const total = subtotal + shippingCost;
      const platformFee = Number((total * PLATFORM_FEE_RATE).toFixed(2));
      const vendorEarnings = Number((total - platformFee).toFixed(2));
      const meta = vendorMeta.get(vendorId);

      return {
        vendorId,
        vendorName: meta?.businessName || `Vendor ${vendorId.slice(0, 6)}`,
        vendorEmail: meta?.email || '',
        items: items.map(mapCartItemToOrderItem),
        subtotal,
        shippingCost,
        total,
        platformFee,
        vendorEarnings,
        status: 'pending',
      };
    });

    const subtotal = cartItems.reduce((sum, item) => sum + item.total, 0);
    const shippingTotal = sumLineShippingCosts(cartItems);
    const totalAmount = subtotal + shippingTotal;
    const now = new Date().toISOString();

    return {
      payload: {
        customer_id: customer.customerId,
        customer_email: customer.customerEmail,
        customer_name: customer.customerName,
        customer_phone: customer.customerPhone,
        shipping_address: shippingAddress,
        items: cartItems.map(mapCartItemToOrderItem),
        vendor_orders: vendorOrders,
        subtotal,
        shipping_total: shippingTotal,
        total_amount: totalAmount,
        payment_intent_id: paymentIntentId,
        payment_status: 'pending' as const,
        status: 'pending' as const,
        updated_at: now,
      },
      vendorOrders,
      subtotal,
      shippingTotal,
      totalAmount,
    };
  }

  private mapPreparedOrder(
    orderId: string,
    customer: OrderCustomer,
    shippingAddress: ShippingAddress,
    paymentIntentId: string,
    vendorOrders: VendorOrder[],
    subtotal: number,
    shippingTotal: number,
    totalAmount: number,
    createdAt?: string
  ): Order {
    const created = createdAt ? new Date(createdAt) : new Date();
    return {
      id: orderId,
      customerId: customer.customerId,
      customerEmail: customer.customerEmail,
      customerName: customer.customerName,
      customerPhone: customer.customerPhone,
      shippingAddress,
      items: vendorOrders.flatMap((vendorOrder) => vendorOrder.items),
      vendorOrders,
      subtotal,
      shippingTotal,
      totalAmount,
      paymentIntentId,
      paymentStatus: 'pending',
      status: 'pending',
      createdAt: created,
      updatedAt: new Date(),
    };
  }

  /** Reuse an open pending checkout order instead of creating duplicates on each Pay click. */
  public async prepareCheckoutOrder(
    cartItems: CartItem[],
    customer: OrderCustomer,
    shippingAddress: ShippingAddress
  ): Promise<Order> {
    const { payload, vendorOrders, subtotal, shippingTotal, totalAmount } =
      await this.buildOrderPayload(cartItems, customer, shippingAddress, '');

    const { data: pendingOrders, error: pendingError } = await supabase
      .from('orders')
      .select('id, created_at')
      .eq('customer_id', customer.customerId)
      .eq('payment_status', 'pending')
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (pendingError) throw pendingError;

    const reusableOrder = pendingOrders?.[0];
    const stalePendingIds = (pendingOrders || []).slice(1).map((row) => row.id);

    if (stalePendingIds.length > 0) {
      const { error: cancelError } = await supabase
        .from('orders')
        .update({
          payment_status: 'failed',
          status: 'cancelled',
          updated_at: new Date().toISOString(),
        })
        .in('id', stalePendingIds)
        .eq('payment_status', 'pending');
      if (cancelError) throw cancelError;
    }

    if (reusableOrder) {
      const { error: updateError } = await supabase
        .from('orders')
        .update(payload)
        .eq('id', reusableOrder.id)
        .eq('payment_status', 'pending');
      if (updateError) throw updateError;

      return this.mapPreparedOrder(
        reusableOrder.id,
        customer,
        shippingAddress,
        '',
        vendorOrders,
        subtotal,
        shippingTotal,
        totalAmount,
        reusableOrder.created_at
      );
    }

    const insertPayload = {
      ...payload,
      created_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from('orders').insert(insertPayload).select('id').single();
    if (error) throw error;

    return this.mapPreparedOrder(
      data.id,
      customer,
      shippingAddress,
      '',
      vendorOrders,
      subtotal,
      shippingTotal,
      totalAmount,
      insertPayload.created_at
    );
  }

  public async createOrder(
    cartItems: CartItem[],
    customer: OrderCustomer,
    shippingAddress: ShippingAddress,
    paymentIntentId: string
  ): Promise<Order> {
    const { payload, vendorOrders, subtotal, shippingTotal, totalAmount } =
      await this.buildOrderPayload(cartItems, customer, shippingAddress, paymentIntentId);

    const insertPayload = {
      ...payload,
      created_at: new Date().toISOString(),
    };
    const { data, error } = await supabase.from('orders').insert(insertPayload).select('id').single();
    if (error) throw error;

    return this.mapPreparedOrder(
      data.id,
      customer,
      shippingAddress,
      paymentIntentId,
      vendorOrders,
      subtotal,
      shippingTotal,
      totalAmount,
      insertPayload.created_at
    );
  }

  public async updatePaymentStatus(orderId: string, status: 'succeeded' | 'failed' | 'pending'): Promise<void> {
    const { error } = await supabase.from('orders').update({
      payment_status: status,
      status: status === 'succeeded' ? 'confirmed' : 'pending',
      updated_at: new Date().toISOString(),
    }).eq('id', orderId);
    if (error) throw error;
  }

  public async getCustomerOrders(customerId: string): Promise<Order[]> {
    const { data: rows, error } = await supabase
      .from('orders')
      .select('*')
      .eq('customer_id', customerId)
      .eq('payment_status', 'succeeded')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(error.message || 'Failed to fetch customer orders');
    }

    return (rows || []).map((data) => mapOrderRow(data));
  }

  public async getVendorOrders(vendorId: string): Promise<Order[]> {
    // RLS policy "orders vendor read" scopes rows to this vendor; filter client-side as well.
    const { data: rows, error } = await supabase
      .from('orders')
      .select('*')
      .eq('payment_status', 'succeeded');
    if (error) {
      throw new Error(error.message || 'Failed to fetch vendor orders');
    }
    return (rows || [])
      .map((data) => mapOrderRow(data))
      .filter((order) => order.vendorOrders.some((vo) => vo.vendorId === vendorId));
  }

  public async getVendorEarnings(vendorId: string): Promise<{
    totalEarnings: number;
    pendingPayouts: number;
    totalOrders: number;
    totalSales: number;
  }> {
    const orders = await this.getVendorOrders(vendorId);
    const vendorSegments = orders
      .map((order) => order.vendorOrders.find((vo) => vo.vendorId === vendorId))
      .filter((segment): segment is VendorOrder => Boolean(segment));

    const totalEarnings = vendorSegments.reduce((sum, segment) => sum + segment.vendorEarnings, 0);
    const pendingPayouts = vendorSegments
      .filter((segment) => segment.status !== 'delivered')
      .reduce((sum, segment) => sum + segment.vendorEarnings, 0);
    const totalSales = vendorSegments.reduce((sum, segment) => sum + segment.total, 0);

    return {
      totalEarnings,
      pendingPayouts,
      totalOrders: vendorSegments.length,
      totalSales,
    };
  }

  public async getAllOrders(): Promise<Order[]> {
    const { data: rows, error } = await supabase
      .from('orders')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      throw new Error(error.message || 'Failed to fetch orders');
    }
    return (rows || []).map((data) => mapOrderRow(data));
  }

  public async getPlatformEarnings(): Promise<{
    totalPlatformFees: number;
    totalVendorPayouts: number;
    totalOrders: number;
    totalRevenue: number;
  }> {
    const { data: snapshot, error } = await supabase
      .from('orders')
      .select('vendor_orders,total_amount')
      .eq('payment_status', 'succeeded');
    if (error) throw error;

    let totalPlatformFees = 0;
    let totalVendorPayouts = 0;
    let totalRevenue = 0;
    let totalOrders = 0;

    (snapshot || []).forEach((data: any) => {
      const vendorOrders = data.vendor_orders || [];
      totalPlatformFees += vendorOrders.reduce((sum: number, vo: any) => sum + (vo.platformFee || 0), 0);
      totalVendorPayouts += vendorOrders.reduce((sum: number, vo: any) => sum + (vo.vendorEarnings || 0), 0);
      totalRevenue += Number(data.total_amount || 0);
      totalOrders += 1;
    });

    return {
      totalPlatformFees,
      totalVendorPayouts,
      totalOrders,
      totalRevenue,
    };
  }
}

export default OrderService;
